import { Form, Formik } from "formik";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  CONSTANT,
  FEE_TYPE,
  FORM_TITLES,
  INITIALVALUES,
  IS_AUTHOR,
  RESPONSE_STATUS,
  STRING_CONSTANT,
} from "../../../constants/constants";
import { addRegistration } from "../../../redux/features/conference-payment/confRegistrationSlice";
import { getRegistrationTypeList } from "../../../redux/features/conference-registration-type-list/conferenceRegistrationTypleListSlice";
import {
  calculateOptFeePayment,
  calculateTotal,
  findConferenceFee,
  getFeePayment,
  getFeesList,
  prepareRegistrationPayload,
  resetOptionalRegistrationFields,
} from "../../../utils/util";
import FormInputField from "../../form/form-input-field";
import FormSelectField from "../../form/form-select-field";
import CircularLoader from "../../loader/circular-loader";
import FormAuthorRegistrationField from "./form-author-registration";
import FormCountrySelectField from "./form-country-select-field";
import FormMobileCountryCodeField from "./form-mobile-country-code";
import FormNumberOfPaperField from "./form-number-of-paper-field";
import FormOptionalRegistrationTypeField from "./form-optional-registration-type";
import FormRegistrationTypeField from "./form-registration-type";
import { validate } from "./validation";
import RegistrationTotal from "./registation-total";

const ConferenceRegistrationForm = (props) => {
  const { conferenceDetails, conferenceFeesList, isPaidRegistration } = props;

  const { id } = useParams();
  const dispatcher = useDispatch();

  const { payment, loading } = useSelector((state) => state.confRegistration);
  const { kuConfiguration } = useSelector((state) => state.conferenceDetails);
  const { registrationTypeList } = useSelector(
    (state) => state.registrationType
  );
  const [initialValues, setInitialValues] = useState(INITIALVALUES);
  const [isAuthor, setIsAuthor] = useState(false);
  const [isEmailDuplicate, setIsEmailDuplicate] = useState(false);
  const [regFees, setRegFees] = useState(0);
  const [optFees, setOptFees] = useState(0);
  const [authorPaperFees, setAuthorPaperFees] = useState(0);
  const [isMember, setMember] = useState(false);
  const [additionalFieldLabel, setAdditionalFieldLabel] = useState(
    t("additional-field")
  );
  const [optionalRegistration, setOptionalRegistration] = useState([]);
  const [tax, setTax] = useState(0);

  useEffect(() => {
    if (conferenceFeesList?.items && conferenceDetails) {
      let values = initialValues;
      if (conferenceDetails.allowMultipleOpts) {
        conferenceFeesList.items?.forEach((fee, index) => {
          let subscription = fee.isMandatory && fee.subscriptionKey;
          if (subscription) {
            values = { ...values, [subscription]: false };
          }
        });
        setInitialValues(values);
      } else {
        setInitialValues({
          ...values,
          [CONSTANT.OPTIONAL_SUBSCRIPTION_RADIO]: STRING_CONSTANT.BLANK,
        });
      }
    }
    dispatcher(getRegistrationTypeList());
  }, [conferenceFeesList]);

  useEffect(() => {
    if (!conferenceDetails?.paperFeature) {
      setInitialValues({
        ...initialValues,
        isAuthor: false,
        authorRegistering: IS_AUTHOR.NO,
      });
    } else if (!conferenceDetails?.showIsAuthorQuestion) {
      setInitialValues({
        ...initialValues,
        isAuthor: true,
        authorRegistering: IS_AUTHOR.YES,
      });
    }
  }, [conferenceDetails]);

  useEffect(() => {
    if (payment !== undefined) {
      if (payment?.Status === RESPONSE_STATUS.SUCCESS) {
        const paymentPageUrl = payment.paymentPageRedirectUrl;
        window.location.href = paymentPageUrl;
      } else if (payment?.status === 409) {
        setIsEmailDuplicate(true);
      } else {
        //Redirect user to error page.
        console.error("Something went wrong", payment?.Status);
      }
    }
  }, [payment]);

  useEffect(() => {
    if (kuConfiguration?.tax) {
      const kuTax = Number(kuConfiguration.tax);
      if (!isNaN(kuTax)) {
        setTax(kuTax / 100);
      }
    }
  }, [kuConfiguration]);

  const handleRegTypeChange = (event, setFieldValue) => {
    //resetting optionalregistration field and value form fees
    resetOptionalRegistrationFields(conferenceFeesList, setFieldValue);
    setOptFees(0);
    const subscriptionKey = event.target.value;
    if (!subscriptionKey) {
      setFieldValue("total", (optFees * 1 + tax + authorPaperFees).toFixed(2));
      setFieldValue("registrationType", STRING_CONSTANT.BLANK);
      setRegFees(0);
      setMember(false);
      return;
    }

    if (conferenceFeesList?.items?.length > 0) {
      const member = conferenceFeesList.items.find(
        (item) =>
          item.showAdditionalField && item.subscriptionKey === subscriptionKey
      );
      if (member) {
        setMember(true);
        setAdditionalFieldLabel(member.additionalFieldLabel);
      } else {
        setMember(false);
      }
    }

    let conferenceFeeObject = findConferenceFee(
      conferenceFeesList,
      subscriptionKey
    );
    if (conferenceFeeObject) {
      let regFeesPayment = getFeePayment(
        conferenceDetails,
        conferenceFeeObject,
        tax,
        FEE_TYPE.REGISTRATION
      );
      setFieldValue("registrationType", subscriptionKey);
      setFieldValue(
        "total",
        ((regFeesPayment + optFees) * (1 + tax) + authorPaperFees).toFixed(2)
      );
      setRegFees(regFeesPayment);
    }
  };

  const handleOneOptionalSubscription = (event, setFieldValue) => {
    const optionalRegistrationValue = event.target.value;
    setFieldValue(
      CONSTANT.OPTIONAL_SUBSCRIPTION_RADIO,
      optionalRegistrationValue
    );
    setFieldValue("optionalRegistration", [optionalRegistrationValue]);
    return calculateOptFeePayment(
      optionalRegistrationValue,
      conferenceFeesList,
      conferenceDetails,
      tax
    );
  };

  const handleMultipleOptionalSubscription = (
    event,
    setFieldValue,
    currentValue,
    optionalValue
  ) => {
    const subscriptionKey = event.target.name;
    setFieldValue(subscriptionKey, currentValue);
    let conferenceFee = findConferenceFee(conferenceFeesList, optionalValue);
    if (!conferenceFee) return null;

    let optFeePayment = getFeePayment(
      conferenceDetails,
      conferenceFee,
      tax,
      FEE_TYPE.OPTIONAL
    );
    if (currentValue) {
      optFeePayment = optFees + optFeePayment;
      setOptionalRegistration([...optionalRegistration, optionalValue]);
      setFieldValue("optionalRegistration", [
        ...optionalRegistration,
        optionalValue,
      ]);
    } else {
      optFeePayment = optFees - optFeePayment;
      const filteredOptionalRegistration = optionalRegistration.filter(
        (item) => item !== optionalValue
      );
      setOptionalRegistration(filteredOptionalRegistration);
      setFieldValue("optionalRegistration", filteredOptionalRegistration);
    }
    if (optFeePayment < 0) {
      return 0;
    }
    return optFeePayment;
  };
  const handleOptionalRegChange = (event, setFieldValue, optionalValue) => {
    const subscriptionKey = event.target.name;
    let optFeePayment;

    if (subscriptionKey === CONSTANT.OPTIONAL_SUBSCRIPTION_RADIO) {
      optFeePayment = handleOneOptionalSubscription(event, setFieldValue);
    } else {
      const currentValue = event.target.checked;
      optFeePayment = handleMultipleOptionalSubscription(
        event,
        setFieldValue,
        currentValue,
        optionalValue
      );
    }

    if (typeof optFeePayment === "number") {
      setOptFees(optFeePayment);
      setFieldValue(
        "total",
        ((optFeePayment + regFees) * (1 + tax) + authorPaperFees).toFixed(2)
      );
    }
  };

  const handleRadioChange = (event, setFieldValue) => {
    const isAuthors = event.target.value === IS_AUTHOR.YES;
    setIsAuthor(isAuthors);
    setFieldValue("isAuthor", isAuthors);
    setFieldValue("authorRegistering", event.target.value);
    setFieldValue("noOfPapers", 0);
    setFieldValue("paperReferences", STRING_CONSTANT.BLANK);
    let authorPaperFees = 0;
    setFieldValue(
      "total",
      ((regFees + optFees) * (1 + tax) + authorPaperFees).toFixed(2)
    );
    setAuthorPaperFees(authorPaperFees);
  };

  const handleAuthorPaperChange = (event, setFieldValue) => {
    const noOfPapersValue = event.target.value;
    const parts = noOfPapersValue.split(STRING_CONSTANT.DOLLAR);
    if (parts.length < 2) {
      setFieldValue("noOfPapers", STRING_CONSTANT.BLANK);
      setFieldValue("paperReferences", STRING_CONSTANT.BLANK);
      setFieldValue("total", ((regFees + optFees) * (1 + tax)).toFixed(2));
      setAuthorPaperFees(0);
      return;
    }
    const authorPaperFeesStr = parts[0];
    const authorPaperFees = parseFloat(authorPaperFeesStr);
    if (isNaN(authorPaperFees)) {
      console.error("Invalid author paper fees value");
      return;
    }
    const total = ((regFees + optFees) * (1 + tax) + authorPaperFees).toFixed(
      2
    );
    setFieldValue("noOfPapers", noOfPapersValue);
    setFieldValue("paperReferences", STRING_CONSTANT.BLANK);
    setFieldValue("total", total);
    setAuthorPaperFees(authorPaperFees);
  };

  const handleTitleChange = (event, setFieldValue) => {
    const title = event.target.value;
    setFieldValue("title", title);
  };

  const submitFormData = (formData, isFreeRegistration) => {
    const registration = prepareRegistrationPayload(
      formData,
      id,
      isFreeRegistration
    );
    setInitialValues(formData);
    dispatcher(addRegistration(registration));
  };

  const handleBlur = async () => {
    setIsEmailDuplicate(false);
  };
  const totalAED = calculateTotal(regFees, optFees, tax, authorPaperFees);
  const totalUSD = (totalAED / Number(kuConfiguration?.aed)).toFixed(2);
  return (
    <>
      {!loading ? (
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validate={validate(registrationTypeList)}
          onSubmit={(values) => {
            let totalAsNumber = parseFloat(values.total);
            if (totalAsNumber === 0) {
              // setIsFreeRegistration(true);
              submitFormData(values, true);
              return;
            }
            submitFormData(values, false);
          }}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <FormSelectField
                label={t("title")}
                options={FORM_TITLES}
                fieldName="title"
                error={errors.title}
                touched={touched.title}
                isRequired={true}
                className="mb-3"
                handleChange={(event) =>
                  handleTitleChange(event, setFieldValue)
                }
              />
              <FormInputField
                label={t("first-name")}
                error={errors.firstName}
                touched={touched.firstName}
                fieldName="firstName"
                type="text"
                isVisible={true}
                isRequired={true}
              />
              <FormInputField
                label={t("last-name")}
                error={errors.lastName}
                touched={touched.lastName}
                fieldName="lastName"
                type="text"
                isVisible={true}
                isRequired={true}
              />
              <FormInputField
                label={t("email")}
                error={errors.userEmail}
                touched={touched.userEmail}
                fieldName="userEmail"
                type="email"
                isVisible={true}
                isRequired={true}
                isEmailDuplicate={isEmailDuplicate}
                onBlur={() => handleBlur()}
              />
              <FormInputField
                label={t("confirm-email")}
                error={errors.confirmEmail}
                touched={touched.confirmEmail}
                fieldName="confirmEmail"
                type="email"
                autoComplete={"off"}
                handleOnPaste={(e) => {
                  e.preventDefault();
                }}
                isVisible={true}
                isRequired={true}
              />
              <FormMobileCountryCodeField
                error={errors.mobile}
                touched={touched.mobile}
              />
              <FormCountrySelectField
                label={t("country")}
                fieldName="country"
                error={errors.country}
                touched={touched.country}
              />
              <FormInputField
                label={t("affiliation")}
                error={errors.affiliation}
                touched={touched.affiliation}
                fieldName="affiliation"
                type="text"
                isVisible={true}
                isRequired={true}
              />
              <FormRegistrationTypeField
                handleRegTypeChange={handleRegTypeChange}
                setFieldValue={setFieldValue}
                feesList={getFeesList(
                  conferenceFeesList?.items,
                  true,
                  isPaidRegistration
                )}
                error={errors.registrationType}
                touched={touched.registrationType}
              />
              <FormInputField
                label={additionalFieldLabel}
                fieldName="membershipNumber"
                type="text"
                isVisible={isMember}
                isRequired={true}
                error={errors.membershipNumber}
                touched={touched.membershipNumber}
              />
              <FormOptionalRegistrationTypeField
                feesList={getFeesList(conferenceFeesList?.items, false)}
                handleOptionalRegChange={handleOptionalRegChange}
                setFieldValue={setFieldValue}
                conferenceDetails={conferenceDetails}
              />
              {conferenceDetails?.paperFeature && (
                <>
                  {conferenceDetails?.showIsAuthorQuestion && (
                    <FormAuthorRegistrationField
                      handleRadioChange={handleRadioChange}
                      setFieldValue={setFieldValue}
                    />
                  )}
                  {(isAuthor || !conferenceDetails?.showIsAuthorQuestion) && (
                    <>
                      <FormNumberOfPaperField
                        label={t("no-of-papers")}
                        fieldName="noOfPapers"
                        error={errors.noOfPapers}
                        touched={touched.noOfPapers}
                        isRequired={true}
                        className="mb-3"
                        handleChange={(event) =>
                          handleAuthorPaperChange(event, setFieldValue)
                        }
                        authorPaperFees={authorPaperFees}
                      />
                      <FormInputField
                        label={t("e-paper-reference")}
                        fieldName="paperReferences"
                        type="text"
                        isVisible={true}
                        isRequired={false}
                      />
                    </>
                  )}
                </>
              )}
              <hr className="opacity-1" />
              <RegistrationTotal
                totalAED={totalAED}
                totalUSD={totalUSD}
                conferenceDetails={conferenceDetails}
              />
              <button
                type="submit"
                className="btn btn-primary mt-3 bg-brand w-100"
              >
                {totalAED.toFixed(2) === "0.00"
                  ? t("complete-registration")
                  : t("proceed-to-payment")}
              </button>
            </Form>
          )}
        </Formik>
      ) : (
        <CircularLoader />
      )}
    </>
  );
};

export default ConferenceRegistrationForm;
